// ===== Technical indicators: SMA, EMA, RSI, MACD, Bollinger Bands, Stochastic =====
export const sma = (src, n) => {
  const out = new Array(src.length).fill(null);
  let sum = 0;
  for (let i = 0; i < src.length; i++) {
    sum += src[i];
    if (i >= n) sum -= src[i - n];
    if (i >= n - 1) out[i] = sum / n;
  }
  return out;
};

export const ema = (src, n) => {
  const out = new Array(src.length).fill(null);
  const k = 2 / (n + 1);
  let prev = null;
  for (let i = 0; i < src.length; i++) {
    if (src[i] == null) continue;
    prev = prev == null ? src[i] : src[i] * k + prev * (1 - k);
    if (i >= n - 1) out[i] = prev;
  }
  return out;
};

export const rsi = (src, n) => {
  const out = new Array(src.length).fill(null);
  let g = 0, l = 0;
  for (let i = 1; i < src.length; i++) {
    const d = src[i] - src[i - 1];
    const up = Math.max(d, 0), dn = Math.max(-d, 0);
    if (i <= n) { g += up / n; l += dn / n; } else { g = (g * (n - 1) + up) / n; l = (l * (n - 1) + dn) / n; }
    if (i >= n) out[i] = l === 0 ? 100 : 100 - 100 / (1 + g / l);
  }
  return out;
};

export const macd = (src, f, s, sig) => {
  const ef = ema(src, f), es = ema(src, s);
  const main = src.map((_, i) => (ef[i] != null && es[i] != null ? ef[i] - es[i] : null));
  const start = main.findIndex((v) => v != null);
  const sg = new Array(src.length).fill(null);
  sma(main.slice(start), sig).forEach((v, i) => (sg[i + start] = v));
  return { main, signal: sg };
};

export const bands = (src, n, dev) => {
  const mid = sma(src, n);
  const up = [], lo = [];
  for (let i = 0; i < src.length; i++) {
    if (mid[i] == null) { up.push(null); lo.push(null); continue; }
    let v = 0;
    for (let j = i - n + 1; j <= i; j++) v += (src[j] - mid[i]) ** 2;
    const sd = Math.sqrt(v / n);
    up.push(mid[i] + dev * sd); lo.push(mid[i] - dev * sd);
  }
  return { mid, up, lo };
};

export const stoch = (bars, k, d, slow) => {
  const raw = bars.map((_, i) => {
    if (i < k - 1) return null;
    let hh = -Infinity, ll = Infinity;
    for (let j = i - k + 1; j <= i; j++) { hh = Math.max(hh, bars[j].h); ll = Math.min(ll, bars[j].l); }
    return hh === ll ? 50 : ((bars[i].c - ll) / (hh - ll)) * 100;
  });
  const start = k - 1;
  const main = new Array(bars.length).fill(null);
  sma(raw.slice(start), slow).forEach((v, i) => (main[i + start] = v));
  const st2 = main.findIndex((v) => v != null);
  const sig = new Array(bars.length).fill(null);
  sma(main.slice(st2), d).forEach((v, i) => (sig[i + st2] = v));
  return { main, signal: sig };
};

// Computes plot lines for an indicator config; returns { overlay, lines:[{values,color,dash,hist}], range, levels }
export const computeIndicator = (ind, bars) => {
  const close = bars.map((b) => b.c);
  switch (ind.type) {
    case "SMA": return { overlay: true, label: `MA(${ind.period})`, lines: [{ values: sma(close, ind.period), color: ind.color }] };
    case "EMA": return { overlay: true, label: `EMA(${ind.period})`, lines: [{ values: ema(close, ind.period), color: ind.color }] };
    case "BB": {
      const b = bands(close, ind.period, ind.dev);
      return { overlay: true, label: `Bands(${ind.period},${ind.dev})`, lines: [{ values: b.mid, color: ind.color }, { values: b.up, color: ind.color }, { values: b.lo, color: ind.color }] };
    }
    case "RSI": return { label: `RSI(${ind.period})`, range: [0, 100], levels: [30, 70], lines: [{ values: rsi(close, ind.period), color: ind.color }] };
    case "MACD": {
      const m = macd(close, ind.fast, ind.slow, ind.signal);
      return { label: `MACD(${ind.fast},${ind.slow},${ind.signal})`, levels: [0], lines: [{ values: m.main, color: ind.color, hist: true }, { values: m.signal, color: "#FF1744", dash: true }] };
    }
    case "STOCH": {
      const s = stoch(bars, ind.k, ind.d, ind.slow);
      return { label: `Stoch(${ind.k},${ind.d},${ind.slow})`, range: [0, 100], levels: [20, 80], lines: [{ values: s.main, color: ind.color }, { values: s.signal, color: "#FF1744", dash: true }] };
    }
    default: return { lines: [] };
  }
};
